import {
  Box,
  Button,
  CircularProgress,
  FormControl,
  FormControlLabel,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  SelectChangeEvent,
  Stack,
  Switch,
  Typography,
} from "@mui/material";
import {
  Control,
  useFieldArray,
  ArrayPath,
  Path,
  FieldValues,
  Controller,
} from "react-hook-form";
import DeleteIcon from "@mui/icons-material/Delete";
import { useEntitySettings } from "../hooks/useEntitySettings";
import { SettingDefinition } from "../../settingDefinition/models/SettingDefinition";
import { EntityTypeLink } from "../models/enums/EntityTypeLink";
import FormTextField from "../../../components/reusable-components/form/MuiFormInput";
import { useFieldRules } from "../../../hooks/useFieldRules";
import RichTextForm from "../../../components/reusable-components/form/RichTextForm";

interface SettingItem {
  key: string;
  value: string | number;
}

interface EntitySettingsFormProps<TFieldValues extends FieldValues> {
  entityType: EntityTypeLink;
  control: Control<TFieldValues>;
  disabled?: boolean;
  fieldName?: ArrayPath<TFieldValues>;
}

const EntitySettingsForm = <TFieldValues extends FieldValues>({
  entityType,
  control,
  disabled = false,
  fieldName = "settings" as ArrayPath<TFieldValues>,
}: EntitySettingsFormProps<TFieldValues>) => {
  const rules = useFieldRules();

  const { fields, append, remove } = useFieldArray<
    TFieldValues,
    typeof fieldName,
    "id"
  >({
    control,
    name: fieldName,
  });

  const {
    fetch: { loading, error },
    data: definitions,
  } = useEntitySettings(entityType);

  const selectedKeys = (fields as unknown as SettingItem[]).map((f) => f.key);

  const availableSettings = definitions.filter(
    (def: SettingDefinition) => !selectedKeys.includes(def.key)
  );

  const handleAddSetting = (event: SelectChangeEvent<string>) => {
    const selectedKey = event.target.value;
    const setting = definitions.find((def) => def.key === selectedKey);
    if (setting) {
      append({
        key: setting.key,
        value: setting.default_value ?? "",
      } as any);
    }
  };

  if (error) {
    return (
      <Typography color="error" sx={{ mt: 2 }}>
        Failed to load settings.
      </Typography>
    );
  }

  return (
    <Box>
      <Typography variant="h4" mb={3} textTransform="capitalize">
        {entityType} Settings
      </Typography>

      {loading ? (
        <Box display="flex" justifyContent="center" py={6}>
          <CircularProgress />
        </Box>
      ) : (
        <Stack spacing={3}>
          {fields.map((field, index) => {
            const fieldKey = (field as unknown as SettingItem).key;
            const setting = definitions.find((s) => s.key === fieldKey);
            const isUnknown = !setting;

            return (
              <Paper
                key={field.id}
                variant="outlined"
                sx={{
                  p: 2,
                  backgroundColor: (theme) =>
                    isUnknown
                      ? theme.palette.error.light
                      : theme.palette.background.paper,
                }}
              >
                <Stack direction="row" alignItems="center" spacing={2}>
                  <Box flex={1}>
                    {setting?.type === "text" || setting?.type === "string" ? (
                      <RichTextForm
                        name={
                          `${fieldName}.${index}.value` as Path<TFieldValues>
                        }
                        label={setting?.description || fieldKey}
                        control={control}
                        disabled={disabled}
                        rules={
                          setting
                            ? rules(setting.description || setting.key)
                                .fromType(setting.type)
                                .get()
                            : undefined
                        }
                      />
                    ) : setting?.type === "boolean" ? (
                      <Controller
                        name={
                          `${fieldName}.${index}.value` as Path<TFieldValues>
                        }
                        control={control}
                        render={({ field }) => (
                          <FormControlLabel
                            control={
                              <Switch
                                {...field}
                                checked={String(field.value) === "1"}
                                onChange={(e) =>
                                  field.onChange(e.target.checked ? "1" : "0")
                                }
                                disabled={disabled}
                              />
                            }
                            label={setting?.description || fieldKey}
                          />
                        )}
                      />
                    ) : (
                      <FormTextField
                        name={
                          `${fieldName}.${index}.value` as Path<TFieldValues>
                        }
                        label={setting?.description || fieldKey}
                        control={control}
                        rules={
                          setting
                            ? rules(setting.description || setting.key)
                                .fromType(setting.type)
                                .get()
                            : undefined
                        }
                        textFieldProps={{
                          disabled,
                          type: setting?.type === "integer" ? "number" : "text",
                        }}
                      />
                    )}
                  </Box>
                  <input
                    type="hidden"
                    name={`${fieldName}.${index}.key`}
                    value={fieldKey}
                  />
                  <IconButton
                    onClick={() => remove(index)}
                    disabled={disabled}
                    color="error"
                    aria-label="Remove setting"
                  >
                    <DeleteIcon />
                  </IconButton>
                </Stack>
              </Paper>
            );
          })}

          {availableSettings.length > 0 ? (
            <FormControl fullWidth>
              <InputLabel id="add-setting-label" disabled={disabled}>
                Add Setting
              </InputLabel>
              <Select
                labelId="add-setting-label"
                value=""
                onChange={handleAddSetting}
                disabled={disabled}
                label="Add Setting"
              >
                {availableSettings.map((setting) => (
                  <MenuItem key={setting.key} value={setting.key}>
                    {setting.description || setting.key}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          ) : (
            <Typography variant="body2" color="text.secondary">
              No more settings to add.
            </Typography>
          )}
        </Stack>
      )}
    </Box>
  );
};

export default EntitySettingsForm;
